/* eslint-disable max-lines-per-function, max-statements */
import prepareComponent from "@bluframe/grapple/prepareComponent"
import {
  AutosuggestProps,
  CriticsAddActionsProps,
  NewsAddItemOptions
} from "@bluframe/mightychroma-mightytool-frontend-types"
import { useCombobox } from "downshift"
import extractFQDN from "helpers/urls/extractFQDN"
import {
  ChangeEvent,
  memo,
  RefObject,
  useCallback,
  useEffect,
  useRef
} from "react"

import AddActions from "./AddActions"

export interface Props extends CriticsAddActionsProps {}

export interface ComponentProps extends Omit<Props, "onAddItem"> {
  additionalInstructionsRef: RefObject<HTMLTextAreaElement | null>
  onAddItem: () => void

  onNewsSourceUrlChange: (event: ChangeEvent<HTMLInputElement>) => void
  onSingleSourceSuggestionClick: AutosuggestProps["onSuggestionClick"]
  newsSourceLinkRef: RefObject<HTMLInputElement | null>
}

/**
 * usePrepareComponent
 *
 * @description Prepares all callbacks and refs used by the Critics AddActions component.
 * @param {Props} props
 * @returns {ComponentProps}
 *
 */
export const usePrepareComponent = ({
  onAddItem: onAddItemProps,
  onClearAll: onClearAllProps,
  searchNewsSource,
  ...props
}: Props): ComponentProps => {
  const additionalInstructionsRef = useRef<HTMLTextAreaElement>(null)

  const newsSourceLinkRef = useRef<HTMLInputElement>(null)
  const pendingSingleSourceAutoSelectRef = useRef(false)

  /**
   * onAddItem
   *
   * @description
   * Handles the addition of a new item by invoking the onAddItem prop with
   * additional instructions if provided.
   * @returns {void}
   *
   */
  const onAddItem = useCallback((): void => {
    const options: NewsAddItemOptions = {
      additionalInstructions: additionalInstructionsRef.current?.value
    }

    if (
      searchNewsSource &&
      searchNewsSource.selectedId &&
      newsSourceLinkRef?.current
    ) {
      options.newsSource = {
        newsSourceId: searchNewsSource.selectedId,
        url: newsSourceLinkRef.current.value
      }
    }

    onAddItemProps(options)
  }, [onAddItemProps, searchNewsSource])

  /**
   * onNewsSourceUrlChange
   *
   * @description Handles the change event for the source URL input field.
   * @param {ChangeEvent<HTMLInputElement>} event
   * @returns {void}
   *
   */
  const onNewsSourceUrlChange: ComponentProps["onNewsSourceUrlChange"] =
    useCallback(
      (event) => {
        const host = extractFQDN(event.currentTarget.value)

        if (host) {
          searchNewsSource?.onGetSuggestions?.({
            inputValue: host,
            type: useCombobox.stateChangeTypes.InputChange
          })
          const autosuggestInput = document.getElementById(
            "search-news-source"
          ) as HTMLInputElement | null
          autosuggestInput?.focus()
          autosuggestInput?.click()
          pendingSingleSourceAutoSelectRef.current = true
        }
      },
      [searchNewsSource]
    )

  /**
   * blurNewsSourceAutosuggest
   *
   * @description Blurs the single-source autosuggest input when auto-selection completes.
   * @returns {void}
   *
   */
  const blurNewsSourceAutosuggest = useCallback((): void => {
    const autosuggestInput = document.getElementById(
      "search-news-source"
    ) as HTMLInputElement | null

    autosuggestInput?.blur()
  }, [])

  useEffect(() => {
    if (!pendingSingleSourceAutoSelectRef.current || !searchNewsSource) {
      return
    }

    if (searchNewsSource.selectedId) {
      pendingSingleSourceAutoSelectRef.current = false
      return
    }

    const suggestions = [
      ...(searchNewsSource.labels ?? []),
      ...(searchNewsSource.outlets ?? [])
    ]

    if (suggestions.length === 1 && newsSourceLinkRef.current) {
      const [suggestion] = suggestions

      if (suggestion?.id) {
        searchNewsSource.onSelectId(
          suggestion.id,
          newsSourceLinkRef.current.value
        )
        blurNewsSourceAutosuggest()
      }

      pendingSingleSourceAutoSelectRef.current = false
      return
    }

    if (suggestions.length > 1) {
      pendingSingleSourceAutoSelectRef.current = false
    }
  }, [
    blurNewsSourceAutosuggest,
    searchNewsSource,
    searchNewsSource?.labels,
    searchNewsSource?.outlets,
    searchNewsSource?.selectedId
  ])

  /**
   * onSingleSourceSuggestionClick
   *
   * @description
   * Handles the click event on a suggestion in the single source autosuggest component.
   * @param {string} id - The ID of the selected suggestion.
   */
  const onSingleSourceSuggestionClick: AutosuggestProps["onSuggestionClick"] =
    useCallback(
      (id) => {
        if (newsSourceLinkRef?.current) {
          searchNewsSource?.onSelectId(id, newsSourceLinkRef?.current.value)
        }
      },
      [searchNewsSource]
    )

  /**
   * onClearNewsSourceUrl
   *
   * @description
   * Cleans the news source URL input field and triggers an input event
   * to update any associated state or listeners.
   * @returns {void}
   *
   */
  const onClearNewsSourceUrl = useCallback((): void => {
    if (newsSourceLinkRef.current) {
      newsSourceLinkRef.current.value = ""
      newsSourceLinkRef.current.dispatchEvent(
        new Event("input", { bubbles: true })
      )
    }
  }, [])

  /**
   * onClearAll
   *
   * @description
   * Handles the clearing of all input fields and invokes the onClearAll prop if provided.
   * @returns {void}
   *
   */
  const onClearAll: Props["onClearAll"] = useCallback(() => {
    onClearNewsSourceUrl()
    onClearAllProps?.()
  }, [onClearAllProps, onClearNewsSourceUrl])

  return {
    ...props,
    additionalInstructionsRef,
    newsSourceLinkRef,
    onAddItem,
    onClearAll,
    onNewsSourceUrlChange,
    onSingleSourceSuggestionClick,
    searchNewsSource
  }
}

export default prepareComponent<Props, ComponentProps>(usePrepareComponent)(
  memo(AddActions)
)
